import React from "react";
import { Link } from "react-router-dom";
import { Camera, Heart, Baby, Flower2, Church, BookOpen, Users, AlertCircle, FileText, MapPin, ArrowRight } from "lucide-react";
import { TARIFS, DEROULEMENT, computeDeposit } from "../mock";

const iconMap = { Camera, Heart, Baby, Flower2, Church, BookOpen, Users };

export default function Tarifs() {
  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 fade-in">
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Prestations</p>
          <h1 className="text-5xl md:text-6xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Tarifs
          </h1>
          <div className="gold-divider" />
        </div>

        {/* Tarifs Cards */}
        <div className="space-y-8">
          {TARIFS.map((tarif) => {
            const Icon = iconMap[tarif.icon] || Camera;
            return (
              <div
                key={tarif.id}
                className="border border-white/10 hover:border-gold/60 transition-colors duration-500 bg-zinc-950/40 p-8 md:p-10"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="shrink-0">
                    {tarif.image ? (
                      <div
                        className="w-full md:w-64 overflow-hidden border border-gold/40 relative self-start"
                        data-testid={`tarif-image-${tarif.id}`}
                      >
                        <img
                          src={tarif.image}
                          alt={tarif.title}
                          loading="lazy"
                          className="w-full h-auto block"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                        <div className="absolute top-2 right-2 w-10 h-10 bg-black/60 border border-gold/40 flex items-center justify-center backdrop-blur-sm">
                          <Icon size={18} className="text-gold" strokeWidth={1.2} />
                        </div>
                      </div>
                    ) : (
                      <div className="w-16 h-16 border border-gold flex items-center justify-center">
                        <Icon size={28} className="text-gold" strokeWidth={1} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 mb-4">
                      <h2 className="text-3xl md:text-4xl italic"
                          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                        {tarif.title}
                      </h2>
                      <p className="text-4xl text-gold"
                         style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                        {tarif.priceDisplay}
                      </p>
                    </div>

                    <ul className="space-y-3 text-gray-300">
                      {tarif.description.map((line, i) => (
                        <li key={`${tarif.id}-d${i}`} className="flex items-start gap-3">
                          <span className="text-gold mt-1.5 shrink-0">•</span>
                          <span className="leading-relaxed">{line}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Deposit info */}
                    <div className="mt-5 flex items-center gap-3 pt-4 border-t border-gold/20">
                      <div className="px-3 py-1.5 bg-gold/10 border border-gold/40 rounded">
                        <span className="text-gold text-xs uppercase tracking-wider">Acompte</span>
                        <span className="text-gold font-bold ml-2">{computeDeposit(tarif)}€</span>
                      </div>
                      <span className="text-gray-500 text-xs italic">obligatoire pour valider la réservation</span>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link
                        to="/reservation"
                        state={{ prestation: tarif.id }}
                        className="btn-gold-filled text-xs"
                      >
                        Réserver
                        <ArrowRight size={14} />
                      </Link>
                      <Link to="/cartes-cadeaux" className="btn-gold text-xs">
                        Offrir
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Deroulement */}
        <div className="mt-24 border border-gold/30 bg-zinc-950/40 p-8 md:p-12">
          <div className="text-center mb-10">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">Comment ça se passe</p>
            <h2 className="text-3xl md:text-4xl section-title"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Déroulement shoot photo
            </h2>
            <div className="gold-divider mt-4" />
          </div>
          <p className="text-gray-400 text-center italic mb-10">
            Comment se déroule un shoot photo que ce soit votre première expérience ou habitué du fait ?
          </p>

          <div className="space-y-6">
            {DEROULEMENT.map((step) => (
              <div key={step.num} className="flex gap-5">
                <div className="shrink-0 w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold"
                     style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: 600 }}>
                  {step.num}
                </div>
                <p className="text-gray-300 leading-relaxed pt-2">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* PS / Important */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-white/10 bg-zinc-950/40 p-6 flex items-start gap-4">
            <FileText size={24} className="text-gold shrink-0 mt-1" strokeWidth={1} />
            <div>
              <h3 className="text-gold uppercase tracking-wider text-xs mb-2">Devis & Factures</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Si besoin, je peux effectuer devis et factures.
              </p>
            </div>
          </div>          <div className="border border-gold/40 bg-gold/5 p-6 flex items-start gap-4">
            <MapPin size={24} className="text-gold shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <h3 className="text-gold uppercase tracking-wider text-xs mb-2">Lieu des séances</h3>
              <p className="text-gray-200 text-sm leading-relaxed">
                Ludovic reçoit à <strong className="text-white">Fos sur Mer</strong>. Pour les séances à plus de <strong className="text-white">20 km</strong>, les frais de trajet (carburant + péage) sont à votre charge.
              </p>
            </div>
          </div>

          <div className="border border-red-500/30 bg-red-500/5 p-6 flex items-start gap-4">
            <AlertCircle size={24} className="text-red-400 shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <h3 className="text-red-400 uppercase tracking-wider text-xs mb-2">Acompte obligatoire</h3>
              <p className="text-gray-200 text-sm leading-relaxed">
                Un <strong className="text-white">acompte de 30%</strong> est demandé lors de la réservation pour bloquer votre créneau. <strong className="text-red-400">Non remboursable en cas d'annulation.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 italic mb-6 text-lg"
             style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem' }}>
            « Pour toutes autres demandes, contactez-moi »
          </p>
          <Link to="/contact" className="btn-gold-filled">
            Me contacter
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
